import { Card, CardActionArea, Box, type CardProps } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { ElementType, ReactNode } from 'react';
import { focusRing } from '../../styles/tokens/semantic.js';

export type SurfaceRole = 'standard' | 'interactive' | 'emphasized' | 'table';
export type SurfacePadding = 'none' | 'standard' | 'comfortable';
export type SurfaceAccentColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

type SurfaceStyleObject = Record<string, unknown>;

export interface SurfaceCardProps extends Omit<CardProps, 'component' | 'variant' | 'padding'> {
  children: ReactNode;
  surfaceRole?: SurfaceRole;
  padding?: SurfacePadding;
  accentColor?: SurfaceAccentColor;
  hoverSx?: SurfaceStyleObject;
  component?: ElementType;
  to?: string;
  'aria-label'?: string;
}

export function SurfaceCard({
  children,
  surfaceRole = 'standard',
  padding = 'standard',
  accentColor,
  hoverSx,
  component,
  to,
  'aria-label': ariaLabel,
  sx,
  ...rest
}: SurfaceCardProps) {
  const theme = useTheme();
  const roleSx = (theme.palette.finance.card[surfaceRole] ?? {}) as SurfaceStyleObject;
  const paddingValue =
    padding === 'none' ? 0 : theme.financeLayout.cardLayout.padding[padding];
  const roleHover = roleSx['&:hover'] as SurfaceStyleObject | undefined;
  const isInteractive = surfaceRole === 'interactive' || Boolean(to);

  // Enhanced hover with lift + shadow for interactive cards
  const enhancedHover: SurfaceStyleObject = isInteractive
    ? {
        ...(roleHover ?? {}),
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[3],
      }
    : roleHover ?? {};

  const mergedHover: SurfaceStyleObject = {
    ...enhancedHover,
    ...(hoverSx ?? {}),
  };

  const externalSx = Array.isArray(sx) ? sx : sx ? [sx] : [];
  const isLink = Boolean(to);

  const baseSx: SurfaceStyleObject = {
    ...roleSx,
    p: isLink ? 0 : paddingValue,
    transition: `transform 200ms ${theme.transitions.easing.easeInOut}, box-shadow 200ms ${theme.transitions.easing.easeInOut}, border-color 150ms ${theme.transitions.easing.easeInOut}`,
    willChange: 'transform',
    ...(accentColor
      ? {
          borderTop: 3,
          borderTopColor: `${accentColor}.main`,
        }
      : {}),
    '&:hover': mergedHover,
    '&:active': isInteractive
      ? {
          transform: 'translateY(0)',
          boxShadow: theme.shadows[1],
        }
      : undefined,
    '&:focus-visible': {
      outline: `${focusRing.width}px ${focusRing.style} ${theme.palette.primary.main}`,
      outlineOffset: `${focusRing.offset}px`,
    },
  };

  return (
    <Card sx={[baseSx, ...externalSx] as SxProps<Theme>} {...rest}>
      {isLink ? (
        <CardActionArea
          component={component as React.ElementType}
          to={to}
          aria-label={ariaLabel}
          sx={{
            p: paddingValue,
            width: '100%',
            height: '100%',
            display: 'block',
            textAlign: 'left',
            color: 'inherit',
            borderRadius: 'inherit',
            '&:focus-visible': {
              outline: `${focusRing.width}px ${focusRing.style} ${theme.palette.primary.main}`,
              outlineOffset: `${focusRing.offset}px`,
            },
          }}
        >
          {children}
        </CardActionArea>
      ) : (
        <Box sx={{ height: '100%' }}>{children}</Box>
      )}
    </Card>
  );
}
